import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Gauge, AlertTriangle, Play, Check, Clock, Image as ImageIcon, X } from "lucide-react";
import api from "../lib/apiClient";
import { RISK_META, fmtDate } from "../lib/gameData";
import { toast } from "sonner";

export default function ReleaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [catchup, setCatchup] = useState(null);
  const [busy, setBusy] = useState(null);
  const [coverPicker, setCoverPicker] = useState(false);
  const [gallery, setGallery] = useState([]);

  const load = useCallback(() => api.get(`/releases/${id}`).then((r) => setD(r.data)).catch(() => navigate("/releases")), [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const openCoverPicker = async () => {
    try { setGallery((await api.get("/content/images")).data); setCoverPicker(true); }
    catch { toast.error("Impossible de charger tes visuels."); }
  };
  const setCover = async (imageId) => {
    try {
      const { data } = await api.post(`/releases/${id}/cover`, { image_id: imageId });
      setD(data); setCoverPicker(false);
      toast.success(imageId ? "Pochette définie ✓" : "Pochette retirée");
    } catch { toast.error("Échec de la mise à jour de la pochette."); }
  };

  const complete = async (taskId) => {
    setBusy(taskId);
    try {
      const { data } = await api.put(`/tasks/${taskId}`, { status: "Terminée" });
      if (data.xp_awarded) toast.success(`+${data.xp_awarded} XP 🎉`);
      (data.new_badges || []).forEach((b) => toast.success(`Badge débloqué : ${b}`));
      load();
    } finally { setBusy(null); }
  };
  const loadCatchup = async () => setCatchup((await api.get(`/releases/${id}/catch-up`)).data);

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <button onClick={() => navigate("/releases")} className="text-sm text-zinc-400 hover:text-amber-400 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Mes sorties</button>

      {/* Header */}
      <div className="bg-gradient-to-br from-amber-500/15 to-transparent border border-amber-500/30 rounded-3xl p-6" data-testid="release-header">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">{d.type}</span>
            <h1 className="font-display text-3xl font-extrabold mt-2">{d.title}</h1>
            <p className="text-sm text-zinc-400 mt-1">Date cible : {fmtDate(d.target_date)}</p>
          </div>
          <div className="text-center">
            <p className="font-display text-4xl font-extrabold gold-text" data-testid="countdown">{d.days_remaining >= 0 ? `J-${d.days_remaining}` : `J+${-d.days_remaining}`}</p>
            <p className="text-xs text-zinc-500">{d.days_remaining >= 0 ? "avant la sortie" : "après la sortie"}</p>
          </div>
        </div>
      </div>

      {/* Cover */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid="release-cover">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-amber-500"><ImageIcon className="w-4 h-4" /><span className="text-xs font-mono uppercase tracking-wider">Pochette officielle</span></div>
          <div className="flex items-center gap-2">
            <button onClick={openCoverPicker} data-testid="release-choose-cover" className="text-xs px-3 h-8 rounded-lg gold-gradient text-black font-semibold">Choisir une pochette</button>
            {d.cover_path && <button onClick={() => setCover(null)} data-testid="release-remove-cover" className="text-xs px-3 h-8 rounded-lg bg-zinc-900 border border-zinc-700 text-zinc-300">Retirer</button>}
          </div>
        </div>
        <div className="mt-4">
          {d.cover_path
            ? <CoverImg path={d.cover_path} className="w-40 h-40 rounded-2xl object-cover border border-zinc-800" />
            : <p className="text-sm text-zinc-500">Aucune pochette. Génère un visuel dans <a href="/content" className="text-amber-400 hover:underline">Content Studio</a>, puis choisis-le ici.</p>}
        </div>
      </div>

      {/* Readiness + Risk */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid="release-readiness">
          <div className="flex items-center justify-between"><span className="text-xs font-mono uppercase tracking-wider text-amber-500 flex items-center gap-2"><Gauge className="w-4 h-4" /> Release Readiness</span><span className="font-display text-2xl font-extrabold">{d.readiness.global}<span className="text-sm text-zinc-500">/100</span></span></div>
          <div className="mt-4 space-y-2">
            {d.readiness.categories.map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-xs">
                <span className="w-24 text-zinc-400">{c.key}</span>
                <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full gold-gradient" style={{ width: `${c.score}%` }} /></div>
                <span className="w-8 text-right font-mono">{c.score}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#18181E] border rounded-3xl p-5" style={{ borderColor: RISK_META[d.risk.level].color + "55" }} data-testid="release-risk">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Release Risk</span>
          <p className="font-display text-2xl font-extrabold mt-2" style={{ color: RISK_META[d.risk.level].color }}>{RISK_META[d.risk.level].dot} {RISK_META[d.risk.level].label}</p>
          <p className="text-sm text-zinc-400 mt-2">{d.risk.explanation}</p>
          <button onClick={loadCatchup} data-testid="catchup-btn" className="mt-4 text-sm text-amber-400 hover:underline">Je suis en retard → plan de rattrapage</button>
          {catchup && (
            <div className="mt-3 text-xs space-y-2" data-testid="catchup-plan">
              <p className="text-zinc-300">{catchup.message}</p>
              <p className="text-red-400">Critique ({catchup.critical.length}) : {catchup.critical.map((t) => t.title).join(", ") || "—"}</p>
              <p className="text-amber-400">Important ({catchup.important.length})</p>
              <p className="text-zinc-500">Optionnel ({catchup.optional.length}) — reportable</p>
            </div>
          )}
        </div>
      </div>

      {/* Next action */}
      {d.next_action && (
        <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid="release-next-action">
          <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Prochaine action</span>
          <div className="flex items-center gap-3 mt-3">
            <div className="flex-1">
              <p className="font-display text-lg font-bold">🎯 {d.next_action.title}</p>
              <p className="text-sm text-zinc-400 mt-1">{d.next_action.why}</p>
            </div>
            <button onClick={() => complete(d.next_action.task_id)} disabled={busy === d.next_action.task_id} data-testid="release-next-complete"
              className="shrink-0 inline-flex items-center gap-2 px-4 h-10 rounded-xl gold-gradient text-black font-semibold disabled:opacity-60">
              {busy === d.next_action.task_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Terminer</>}
            </button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid="release-timeline">
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Timeline</span>
        <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
          {d.timeline.map((m) => (
            <div key={m.label} className="shrink-0 text-center bg-zinc-900 border border-zinc-800 rounded-2xl px-4 py-3 min-w-[92px]">
              <p className="font-display font-extrabold text-amber-400">{m.label}</p>
              <p className="text-[10px] text-zinc-500 mt-1">{m.phase}</p>
              <p className="text-[10px] text-zinc-600">{fmtDate(m.date)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Critical tasks */}
      {d.critical_tasks.length > 0 && (
        <div className="bg-[#18181E] border border-red-500/30 rounded-3xl p-5" data-testid="release-critical">
          <span className="text-xs font-mono uppercase tracking-wider text-red-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Tâches critiques</span>
          <div className="mt-3 space-y-2">
            {d.critical_tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-3 bg-[#0F0F13] border border-zinc-800 rounded-xl p-3" data-testid={`crit-${t.id}`}>
                <span className="flex-1 text-sm truncate">{t.title} {t.overdue && <span className="text-red-400 text-xs">· en retard</span>}</span>
                {t.duration_min ? <span className="text-xs text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" />{t.duration_min}m</span> : null}
                <button onClick={() => complete(t.id)} disabled={busy === t.id} className="text-emerald-400 hover:text-emerald-300"><Check className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All tasks */}
      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid="release-tasks">
        <span className="text-xs font-mono uppercase tracking-wider text-amber-500">Toutes les tâches ({d.tasks.length})</span>
        <div className="mt-3 space-y-1.5">
          {d.tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-3 text-sm py-1.5 border-b border-zinc-900 last:border-0" data-testid={`rtask-${t.id}`}>
              <span className={`flex-1 truncate ${t.status === "Terminée" ? "line-through text-zinc-600" : ""}`}>{t.title}</span>
              <span className="text-[10px] text-zinc-600">{t.phase}</span>
              <span className="text-xs text-zinc-500">{fmtDate(t.deadline)}</span>
              {t.status !== "Terminée" && <button onClick={() => complete(t.id)} disabled={busy === t.id} className="text-emerald-400"><Check className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      </div>
      {coverPicker && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="cover-picker-modal" onClick={() => setCoverPicker(false)}>
          <div className="w-full max-w-lg max-h-[80vh] overflow-y-auto bg-[#18181E] border border-zinc-800 rounded-3xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold">Choisir une pochette</h3>
              <button onClick={() => setCoverPicker(false)} data-testid="cover-picker-close" className="text-zinc-400 hover:text-zinc-200"><X className="w-5 h-5" /></button>
            </div>
            {gallery.length === 0
              ? <p className="text-sm text-zinc-500 py-8 text-center">Aucun visuel. Crée-en un dans <a href="/content" className="text-amber-400 hover:underline">Content Studio</a>.</p>
              : <div className="grid grid-cols-3 gap-2">
                  {gallery.map((img) => (
                    <button key={img.id} onClick={() => setCover(img.id)} data-testid={`cover-pick-${img.id}`} className="rounded-xl overflow-hidden border border-zinc-800 hover:border-amber-500 transition-colors">
                      <CoverImg path={img.media_path} className="w-full aspect-square object-cover" />
                    </button>
                  ))}
                </div>}
          </div>
        </div>
      )}
    </div>
  );
}

function CoverImg({ path, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoked = false; let obj;
    api.get(`/media/${path}`, { responseType: "blob" }).then((res) => { if (!revoked) { obj = URL.createObjectURL(res.data); setSrc(obj); } }).catch(() => {});
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [path]);
  if (!src) return <div className={`${className} bg-zinc-900 flex items-center justify-center`}><Loader2 className="w-5 h-5 text-amber-500 animate-spin" /></div>;
  return <img src={src} alt="pochette" className={className} />;
}
